// SPDX-FileCopyrightText: Copyright (c) 2025-2026 provide.io llc. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
/**
 * Runtime sampling policy — mirrors Python provide.telemetry.sampling.
 */
import { ConfigurationError } from './exceptions';
import { _droppedField, _incrementHealth } from './health';
const DEFAULT_POLICY = { defaultRate: 1.0 };
let _policies = {};
const VALID_SIGNALS = new Set(['logs', 'traces', 'metrics']);
function _validateSignal(signal) {
    if (!VALID_SIGNALS.has(signal)) {
        throw new ConfigurationError(`unknown signal "${signal}", expected one of [logs, metrics, traces]`);
    }
}
function _clamp(rate) {
    return Math.max(0, Math.min(1, rate));
}
export function setSamplingPolicy(signal, policy) {
    _validateSignal(signal);
    _policies[signal] = {
        defaultRate: _clamp(policy.defaultRate),
        overrides: policy.overrides
            ? Object.fromEntries(Object.entries(policy.overrides).map(([k, v]) => [k, _clamp(v)]))
            : undefined,
    };
}
export function getSamplingPolicy(signal) {
    _validateSignal(signal);
    const _policy = _policies[signal] ?? DEFAULT_POLICY;
    return {
        defaultRate: _policy.defaultRate,
        overrides: _policy.overrides ? { ..._policy.overrides } : undefined,
    };
}
export function shouldSample(signal, key) {
    _validateSignal(signal);
    const _policy = _policies[signal] ?? DEFAULT_POLICY;
    const overrides = _policy.overrides;
    // Only consult the override map when an explicit non-null key is provided.
    // Using `key ?? signal` would cause any override keyed by signal name (e.g. "logs")
    // to silently apply to all unkeyed shouldSample("logs") calls — a shadow-override hazard.
    // Stryker disable next-line ConditionalExpression: equivalent mutant — `true && overrides && key in overrides` short-circuits identically to `key != null && ...` because null/undefined are never valid string keys in overrides
    const rate = key != null && overrides && key in overrides ? overrides[key] : _policy.defaultRate;
    const clamped = _clamp(rate);
    /* Stryker disable ConditionalExpression,EqualityOperator,BlockStatement: boundary not observable (Math.random [0,1)); health counter updates tested but perTest coverage misattributes */
    if (clamped <= 0) {
        _incrementHealth(_droppedField(signal));
        return false;
    }
    if (clamped >= 1) {
        // Do not increment emitted here — emitted is incremented downstream at the actual
        // emission site (logger, tracer, etc.) to avoid double-counting.
        return true;
    }
    /* Stryker restore ConditionalExpression,EqualityOperator,BlockStatement */
    // Stryker disable next-line EqualityOperator: Math.random() is in [0,1) so < 1.0 and <= 1.0 are equivalent (random never equals 1.0)
    const sampled = Math.random() < clamped;
    if (!sampled) {
        _incrementHealth(_droppedField(signal));
    }
    return sampled;
}
export function _resetSamplingForTests() {
    _policies = {};
}
